/**
 * Topic-driven team suggestion (NOT random). Fuzzy-matches agents to the user's
 * interest, then picks greedily with a per-domain cap so the suggested group always
 * has contrasting perspectives. Runs client-side — instant, free, no LLM latency.
 */
import { GROUP_MAX, type Specialist, type MemberSpec } from './types';

export const toMember = (s: Specialist): MemberSpec => ({
  id: s.id, name: s.name, persona: s.persona, avatar_icon: s.avatar_icon,
  profile_id: s.id, model_tier: s.model_tier, temperature: s.temperature,
});

const domOf = (s: Specialist): string => (s as { domain?: string }).domain || 'general';

/**
 * Topic → domain affinity. Natural-language goals ("launching a SaaS", "a 30s reel") rarely
 * share literal tokens with an agent's capabilities, so a pure fuzzy match scores ~0 and the
 * greedy pick falls back to catalog order (always the first domain). These keyword sets map a
 * topic to the RIGHT domain(s) so "Build my team" forms a relevant team per category. Match is
 * substring-based (so "launch"/"launching" both hit). Add words here as the catalog grows.
 */
const DOMAIN_KEYWORDS: Record<string, string[]> = {
  startup: ['startup', 'saas', 'founder', 'mvp', 'launch', 'pitch', 'fundrais', 'gtm', 'go-to-market', 'go to market', 'product-market', 'product market', 'seed', 'venture', 'wedge', 'icp', 'unit economics', 'b2b', 'bootstrap'],
  markets: ['macro', 'rates', 'stock', 'equit', 'crypto', 'bond', 'fed', 'inflation', 'prediction market', 'polymarket', 'trade', 'trading', 'portfolio', 'hedge', 'recession', 'yield', 'invest', 'market'],
  media: ['reel', 'video', 'tiktok', 'youtube', 'shorts', 'image', 'photo', 'logo', 'music', 'song', 'track', 'audio', 'voiceover', 'voice over', 'animation', 'animate', 'clip', 'thumbnail', 'podcast', 'film', 'movie'],
  marketing: ['marketing', 'campaign', 'brand', 'copy', 'ad ', 'ads', 'advert', 'seo', 'social media', 'positioning', 'messaging', 'growth', 'audience', 'newsletter', 'landing page'],
  research: ['research', 'literature', 'survey', 'evidence', 'whitepaper', 'consulting', 'framework', 'due diligence', 'report'],
  datascience: ['dataset', 'data science', 'machine learning', 'ml model', 'statistic', 'regression', 'forecast', 'analytics', 'data analysis', 'neural', 'classifier'],
  study: ['study', 'exam', 'learn', 'course', 'homework', 'essay', 'revision', 'tutor', 'school', 'university', 'test prep', 'lecture', 'quiz'],
  creative: ['novel', 'story', 'sci-fi', 'sci fi', 'fantasy', 'worldbuild', 'plot', 'character', 'screenplay', 'fiction', 'book', 'narrative'],
  engineering: ['code', 'coding', 'app', 'api', 'bug', 'software', 'architecture', 'backend', 'frontend', 'database', 'deploy', 'refactor', 'programming'],
  lifestyle: ['travel', 'fitness', 'recipe', 'cook', 'workout', 'diet', 'wellness', 'trip', 'budget', 'meal'],
};

/** Domains whose keywords appear in the topic (substring match on the lowercased topic). */
export function topicDomains(topic: string): Set<string> {
  const t = (topic || '').toLowerCase();
  const hit = new Set<string>();
  for (const [domain, words] of Object.entries(DOMAIN_KEYWORDS)) {
    if (words.some((w) => t.includes(w))) hit.add(domain);
  }
  return hit;
}

/** A "challenger" agent (contrarian/critic/skeptic) — the voice that keeps debate honest. */
const CHALLENGER = /contrarian|risk|critique|red[ -]?team|devil|stress|advocate|skeptic|adversar/i;
function isChallenger(s: Specialist): boolean {
  return CHALLENGER.test(`${(s.capabilities || []).join(' ')} ${s.persona || ''}`);
}

/** Score an agent against a topic. Weights: capabilities > persona > description. */
export function fuzzyScore(agent: Specialist, query: string): number {
  const words: string[] = query.toLowerCase().match(/[a-z]{3,}/g) || [];
  if (!words.length) return 0;
  const cap = (agent.capabilities || []).join(' ').toLowerCase();
  const mid = (agent.persona || '').toLowerCase();
  const low = (agent.description || '').toLowerCase();
  return words.reduce((n, w) => n + (cap.includes(w) ? 2 : 0) + (mid.includes(w) ? 1.5 : 0) + (low.includes(w) ? 1 : 0), 0);
}

/**
 * Suggest a team for a topic — relevant AND contrasting (not random).
 * 1) Greedy over fuzzy score, capping each domain at 2 for breadth.
 * 2) Ensure at least a pair, even on a weak match.
 * 3) Inject a challenger voice (contrarian/critic) when there's room and none yet —
 *    so the discussion has built-in tension instead of an echo chamber.
 */
export function suggestTeam(all: Specialist[], topic: string, size = 4): MemberSpec[] {
  const cap = Math.min(size, GROUP_MAX);
  // Combine token overlap with DOMAIN AFFINITY so a natural goal ("launching a SaaS") routes to
  // the right domain even when no literal word matches an agent's capabilities. A domain hit is
  // weighted heavily (3) so the team is on-category; fuzzy score then orders within/across domains.
  const domains = topicDomains(topic);
  const scored = all
    .map((s) => ({ s, score: fuzzyScore(s, topic) + (domains.has(domOf(s)) ? 3 : 0) }))
    .sort((a, b) => b.score - a.score);

  const domainCount: Record<string, number> = {};
  const picked: Specialist[] = [];
  for (const { s, score } of scored) {
    if (picked.length >= cap) break;
    if (score === 0 && picked.length > 0) break; // stop once relevance runs out
    if ((domainCount[domOf(s)] || 0) >= 2) continue; // diversity guard
    domainCount[domOf(s)] = (domainCount[domOf(s)] || 0) + 1;
    picked.push(s);
  }
  if (picked.length < 2) {
    for (const { s } of scored) {
      if (picked.length >= cap) break;
      if (!picked.some((p) => p.id === s.id)) picked.push(s);
    }
  }
  // Liven the debate: add a challenger (prefer one from a domain already in the team).
  if (picked.length >= 2 && picked.length < cap && !picked.some(isChallenger)) {
    const doms = new Set(picked.map(domOf));
    const challenger =
      all.find((s) => isChallenger(s) && !picked.some((p) => p.id === s.id) && doms.has(domOf(s)))
      || all.find((s) => isChallenger(s) && !picked.some((p) => p.id === s.id));
    if (challenger) picked.push(challenger);
  }
  return picked.map(toMember);
}
